'use client'

import { memo, useRef } from 'react'
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion'
import { CRAFT_MEDIA } from '@/app/ninxl/_components/craft-media'

/** baby: Model chips revealed as Namaste hands open. */
const REVEALS = [
  {
    id: 'weights',
    label: 'On-device model',
    src: CRAFT_MEDIA.adam.modelWeights,
    className: 'nin-namaste__chip--weights',
  },
  {
    id: 'device',
    label: 'Local inference',
    src: CRAFT_MEDIA.adam.modelDevice,
    className: 'nin-namaste__chip--device',
  },
  {
    id: 'agent',
    label: 'Private agent',
    src: CRAFT_MEDIA.adam.modelAgent,
    className: 'nin-namaste__chip--agent',
  },
] as const

type NinCraftNamasteProps = {
  /** baby: Optional progress override for tests. */
  progress?: MotionValue<number> | null
}

/**
 * baby: /nin Namaste scroll: opposite of Adam.
 * Hands start pressed together (true namaste, no seam gap), then open outward
 * to reveal on-device model / local inference / private agent.
 */
export const NinCraftNamaste = memo(function NinCraftNamaste({
  progress: progressOverride = null,
}: NinCraftNamasteProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start end', 'end start'],
  })

  const progress = progressOverride ?? scrollYProgress

  // baby: Hold closed (true namaste) until mid scrub, then open outward.
  const leftX = useTransform(progress, [0, 0.28, 0.72], ['0%', '0%', '-36%'])
  const rightX = useTransform(progress, [0, 0.28, 0.72], ['0%', '0%', '36%'])
  const coreOpacity = useTransform(progress, [0.3, 0.55], [0, 1])
  const coreScale = useTransform(progress, [0.3, 0.55], [0.72, 1])
  const captionOpacity = useTransform(progress, [0.26, 0.5], [0, 1])
  const captionY = useTransform(progress, [0.26, 0.5], [10, 0])
  const chipOpacity = useTransform(progress, [0.4, 0.68], [0, 1])
  const chipScale = useTransform(progress, [0.4, 0.68], [0.7, 1])

  return (
    <div
      ref={trackRef}
      className="nin-namaste"
      data-testid="nin-craft-namaste"
      aria-label="Namaste opens to on-device AI"
    >
      <div className="nin-namaste__sticky">
        <motion.p
          className="nin-namaste__caption"
          style={
            reduceMotion ? undefined : { opacity: captionOpacity, y: captionY }
          }
        >
          <span className="nin-namaste__caption-strong">On-device AI</span>{' '}
          <span className="nin-namaste__caption-soft">opens on your machine</span>
        </motion.p>

        <div className="nin-namaste__stage">
          {/* baby: Core sits under hands; revealed as palms part. */}
          <div className="nin-namaste__core-wrap">
            <motion.ul
              className="nin-namaste__chips"
              style={
                reduceMotion
                  ? undefined
                  : { opacity: chipOpacity, scale: chipScale }
              }
            >
              {REVEALS.map((chip) => (
                <li key={chip.id} className={`nin-namaste__chip ${chip.className}`}>
                  <img
                    src={chip.src}
                    alt=""
                    aria-hidden
                    draggable={false}
                    className="nin-namaste__chip-img"
                  />
                  <span className="nin-namaste__chip-label">{chip.label}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              className="nin-namaste__core"
              style={
                reduceMotion
                  ? undefined
                  : { opacity: coreOpacity, scale: coreScale }
              }
            >
              <img
                className="nin-namaste__model"
                src={CRAFT_MEDIA.adam.modelCore}
                alt=""
                aria-hidden
                draggable={false}
              />
              <span className="nin-namaste__label">Local model</span>
            </motion.div>
          </div>

          {/* baby: Anchor wrappers keep palms on the center seam; motion only shifts X. */}
          <div className="nin-namaste__hand nin-namaste__hand--left">
            <motion.img
              className="nin-namaste__hand-img"
              src={CRAFT_MEDIA.namaste.handLeft}
              alt=""
              aria-hidden
              draggable={false}
              style={reduceMotion ? { x: '-22%' } : { x: leftX }}
            />
          </div>
          <div className="nin-namaste__hand nin-namaste__hand--right">
            <motion.img
              className="nin-namaste__hand-img"
              src={CRAFT_MEDIA.namaste.handRight}
              alt=""
              aria-hidden
              draggable={false}
              style={reduceMotion ? { x: '22%' } : { x: rightX }}
            />
          </div>
        </div>
      </div>
    </div>
  )
})

NinCraftNamaste.displayName = 'NinCraftNamaste'
